/**
 * 编辑域 —— 「谁能改委托书的哪一块」的唯一定义，前后端共用。
 *
 * 背景：各角色**并行**工作，互不阻塞（设计师在等审批时，面料采购照样录面料）。
 * 所以不能再用「整单状态 + sheet:update」一刀切地判断能不能编辑，
 * 而是把委托书切成若干块，每块有自己的负责角色和自己的可写条件。
 *
 * 设计说明见 `docs/user-roles-and-approval-plan.md` 第四、五节。
 *
 * 三条规则：
 *   1. 单据作废（CANCELLED）→ 一律不可写；
 *   2. 角色不在该域的负责人里 → 不可写（系统管理员除外）；
 *   3. `approvalBound` 的域在**审批中**（PENDING）不可写 —— 正在审的东西不能变。
 *      通过之后仍可改，但一改批准就失效、要重新提交（在服务端自动回退到 DRAFT）。
 */
import { ROLE } from './roles.js';
import { technicalOwner } from './responsibility.js';
import { ATTACHMENT_SECTION, SHEET_SECTION, STAGE, isSheetSectionEnabled } from './sheet-sections.js';
import { DESIGN_SHEET_STATUS } from '../types/design-sheet.js';
export const EDIT_SCOPE = {
    /** 01 设计：图示图稿 / 主信息 / 设计 Memo / 详细图片 */
    DESIGN: 'DESIGN',
    /** 商品企划与设计师维护品名、年度、季节、性别，保留原始推送。 */
    SOURCE_INFO: 'SOURCE_INFO',
    /** 商品企划在工作文档纠正数量；不改变颜色尺码范围。 */
    ORDER_QUANTITY: 'ORDER_QUANTITY',
    /** 版型模块及引用单号归当前款版师。 */
    PATTERN_INFO: 'PATTERN_INFO',
    /** 成衣供应商由生产管理维护，不属于设计审批。 */
    PRODUCTION_INFO: 'PRODUCTION_INFO',
    /** 商品企划维护手工款颜色与尺码范围；设计审批中锁定。 */
    MANUAL_STYLE: 'MANUAL_STYLE',
    /** 手工售价、数量与订货参数，商品企划维护，不撤销设计批准。 */
    MANUAL_COMMERCE: 'MANUAL_COMMERCE',
    /** 02 ColorWay 配色 */
    COLORWAY: 'COLORWAY',
    /** 08 USP·设计意图 */
    USP: 'USP',
    /** 主材料的「面料本身」列：使用部位 / 分类 / 代码 / 名称 / 成分 / 后整理 / 供应商 / 适用颜色 */
    FABRIC_BASE: 'FABRIC_BASE',
    /** 主材料的「生产相关」列：幅宽 / 单位 / 重量（第 7 步） */
    FABRIC_PROD: 'FABRIC_PROD',
    /** 02 辅材料 BOM 的结构与属性 */
    TRIM: 'TRIM',
    /** Pattern：辅料颜色、规格与技术备注；不改变辅料代码和主行结构。 */
    TRIM_TECHNICAL: 'TRIM_TECHNICAL',
    /** 面料单耗：版师、生产管理、面辅料采购均可维护。 */
    CONSUMPTION: 'CONSUMPTION',
    /** 辅料用量：设计师初填，版师核对；不授予面料用量或价格权限。 */
    TRIM_CONSUMPTION: 'TRIM_CONSUMPTION',
    /** 单价：主材料与辅材料的单价与币种（成本计算的输入） */
    PRICE: 'PRICE',
    /** 成本录入：采购模式、FOB 报价、加工费、外包工程费、通关费，以及冻结成本快照 */
    COST: 'COST',
    /** Pattern：基准尺寸、分尺码值与测量方法（第 9 步） */
    SIZE_SPEC: 'SIZE_SPEC',
    /** CAD：尺寸偏差与公差。 */
    SIZE_DEVIATION: 'SIZE_DEVIATION',
    /** @deprecated 数量为只读快照，不再有人工确认写入 */
    QUANTITY: 'QUANTITY',
    /** 商品企划/生产管理维护入库要求日；数量另由 ORDER_QUANTITY 管理。 */
    ORDER_INFO: 'ORDER_INFO',
    /** 07 在线商品信息 */
    ONLINE: 'ONLINE',
    /** 06 QC 委托书 */
    QC: 'QC',
    /** 05 Tech Pack 最终确认；保留原代码名兼容已有完成标记，PDF 按查看权限输出。 */
    TECHPACK_EXPORT: 'TECHPACK_EXPORT',
};
export const EDIT_SCOPES = [
    { scope: EDIT_SCOPE.DESIGN, roles: [ROLE.DESIGNER], approvalBound: true },
    { scope: EDIT_SCOPE.SOURCE_INFO, roles: [ROLE.PLANNER, ROLE.DESIGNER], approvalBound: false },
    { scope: EDIT_SCOPE.ORDER_QUANTITY, roles: [ROLE.PLANNER], approvalBound: false },
    { scope: EDIT_SCOPE.PATTERN_INFO, roles: [ROLE.PATTERN], approvalBound: false },
    { scope: EDIT_SCOPE.PRODUCTION_INFO, roles: [ROLE.PRODUCTION], approvalBound: false },
    { scope: EDIT_SCOPE.MANUAL_STYLE, roles: [ROLE.PLANNER], approvalBound: true },
    { scope: EDIT_SCOPE.MANUAL_COMMERCE, roles: [ROLE.PLANNER], approvalBound: false },
    { scope: EDIT_SCOPE.COLORWAY, roles: [ROLE.DESIGNER], approvalBound: true },
    { scope: EDIT_SCOPE.USP, roles: [ROLE.DESIGNER], approvalBound: true },
    // 生产管理是面料采购的超集：面料采购只有 FABRIC_BASE，生产管理两个都有。
    // 设计师也能选面料（2026-09-25 确认）；面料库在另行开发，接上后从库里选。
    // 不受审批约束：面料不在设计审批的范围里，审批中照样能改，改了也不让批准失效。
    { scope: EDIT_SCOPE.FABRIC_BASE, roles: [ROLE.FABRIC, ROLE.PRODUCTION, ROLE.DESIGNER], approvalBound: false },
    { scope: EDIT_SCOPE.FABRIC_PROD, roles: [ROLE.PRODUCTION, ROLE.FABRIC], approvalBound: false },
    { scope: EDIT_SCOPE.TRIM, roles: [ROLE.PRODUCTION, ROLE.FABRIC, ROLE.DESIGNER], approvalBound: false },
    { scope: EDIT_SCOPE.TRIM_TECHNICAL, roles: [ROLE.DESIGNER, ROLE.PATTERN], approvalBound: false },
    { scope: EDIT_SCOPE.CONSUMPTION, roles: [ROLE.PATTERN, ROLE.PRODUCTION, ROLE.FABRIC], approvalBound: false },
    { scope: EDIT_SCOPE.TRIM_CONSUMPTION, roles: [ROLE.DESIGNER, ROLE.PATTERN], approvalBound: false },
    // 面料采购部统筹全公司面辅料，价格归它（2026-09-23 确认）；设计师只看不改
    { scope: EDIT_SCOPE.PRICE, roles: [ROLE.FABRIC, ROLE.PRODUCTION], approvalBound: false },
    // 加工费等由生产管理录（草案默认，待业务确认 —— designer-requests-plan 第七节）
    { scope: EDIT_SCOPE.COST, roles: [ROLE.PRODUCTION], approvalBound: false },
    { scope: EDIT_SCOPE.SIZE_SPEC, roles: [ROLE.PATTERN], approvalBound: false },
    { scope: EDIT_SCOPE.SIZE_DEVIATION, roles: [ROLE.PATTERN], approvalBound: false },
    { scope: EDIT_SCOPE.QUANTITY, roles: [], approvalBound: false },
    { scope: EDIT_SCOPE.ONLINE, roles: [ROLE.PLANNER], approvalBound: false },
    { scope: EDIT_SCOPE.ORDER_INFO, roles: [ROLE.PLANNER, ROLE.PRODUCTION], approvalBound: false },
    { scope: EDIT_SCOPE.QC, roles: [ROLE.PRODUCTION], approvalBound: false },
    { scope: EDIT_SCOPE.TECHPACK_EXPORT, roles: [ROLE.PRODUCTION], approvalBound: false },
];
export function editScopeDef(scope) {
    const def = EDIT_SCOPES.find((s) => s.scope === scope);
    if (!def)
        throw new Error(`未知的编辑域: ${scope}`);
    return def;
}
/** 灰度关闭的分节不能通过旧页面、直连接口或管理员权限继续写入。 */
export function isEditScopeEnabled(scope) {
    // Launch: quantities are supplied only by SAP / order intake, never manually edited in DS.
    if (scope === EDIT_SCOPE.ORDER_QUANTITY)
        return false;
    if (scope === EDIT_SCOPE.QC)
        return isSheetSectionEnabled(SHEET_SECTION.QC);
    if (scope === EDIT_SCOPE.ONLINE)
        return isSheetSectionEnabled(SHEET_SECTION.ONLINE);
    if (scope === EDIT_SCOPE.USP)
        return isSheetSectionEnabled(SHEET_SECTION.USP);
    return true;
}
export const ENABLED_EDIT_SCOPES = EDIT_SCOPES.filter((s) => isEditScopeEnabled(s.scope));
/**
 * 能不能改这一块。前后端调的是同一个函数 ——
 * 界面上置灰的条件与接口放行的条件必须完全一致，不能各判各的。
 */
export function canEditScope(scope, role, status, assignment) {
    if (!isEditScopeEnabled(scope))
        return false;
    if (status === DESIGN_SHEET_STATUS.CANCELLED)
        return false;
    if (role === ROLE.ADMIN)
        return true;
    const def = editScopeDef(scope);
    if (!role || !def.roles.includes(role))
        return false;
    // 不传上下文仅用于权限说明；真实委托书的前后端始终传入实际担当。
    const technicalRole = technicalChargeOfScope(scope);
    if (technicalRole && role === ROLE.PATTERN && assignment && (!assignment.username
        || technicalOwner(assignment.charges) !== assignment.username))
        return false;
    // 审批中：设计内容锁定，等室长裁决
    if (def.approvalBound && status === DESIGN_SHEET_STATUS.PENDING)
        return false;
    return true;
}
export function technicalChargeOfScope(scope) {
    if (scope === EDIT_SCOPE.TRIM_TECHNICAL || scope === EDIT_SCOPE.TRIM_CONSUMPTION)
        return 'PATTERN';
    if (scope === EDIT_SCOPE.SIZE_SPEC || scope === EDIT_SCOPE.PATTERN_INFO)
        return 'PATTERN';
    if (scope === EDIT_SCOPE.CONSUMPTION || scope === EDIT_SCOPE.SIZE_DEVIATION)
        return 'CAD';
    return null;
}
/** 受设计审批约束的内容，包括商品企划维护的手工款范围。 */
export const DESIGN_APPROVAL_SCOPES = ENABLED_EDIT_SCOPES.filter((s) => s.approvalBound).map((s) => s.scope);
/**
 * 打分节完成标记时，按「哪一个标记」而不是「哪一节」判断 ——
 * 面辅料的各完成项分别对应设计、采购、生产、Pattern 和 CAD 的产出。
 */
export function scopeOfStage(section, stage) {
    if (section === SHEET_SECTION.MATERIAL) {
        if (stage === STAGE.DESIGNER_DONE)
            return EDIT_SCOPE.COLORWAY;
        // 面辅料采购完成：单价是采购的产出，谁能录单价谁能打（面辅料采购、生产管理）
        if (stage === STAGE.PURCHASE_DONE)
            return EDIT_SCOPE.PRICE;
        if (stage === STAGE.PRODUCTION_REVIEWED)
            return EDIT_SCOPE.FABRIC_PROD;
        if (stage === STAGE.TRIM_TECHNICAL_DONE)
            return EDIT_SCOPE.TRIM_TECHNICAL;
        return EDIT_SCOPE.CONSUMPTION; // USAGE_ENTERED
    }
    if (section === SHEET_SECTION.PATTERN)
        return stage === STAGE.SIZE_SPEC_DONE ? EDIT_SCOPE.SIZE_DEVIATION : EDIT_SCOPE.SIZE_SPEC;
    if (section === SHEET_SECTION.QUANTITY)
        return EDIT_SCOPE.QUANTITY;
    if (section === SHEET_SECTION.TECHPACK)
        return EDIT_SCOPE.TECHPACK_EXPORT;
    if (section === SHEET_SECTION.QC)
        return EDIT_SCOPE.QC;
    if (section === SHEET_SECTION.ONLINE)
        return EDIT_SCOPE.ONLINE;
    if (section === SHEET_SECTION.USP)
        return EDIT_SCOPE.USP;
    return EDIT_SCOPE.DESIGN;
}
/** Entering fabric usage and confirming all fabric/trim consumption are different tasks.
 * Production/purchasing may correct fabric usage; the assigned pattern user checks fabric consumption only. */
export function isStageConfirmingRole(section, stage, role) {
    if (role === ROLE.ADMIN)
        return true;
    if (!role)
        return false;
    if (section === SHEET_SECTION.MATERIAL && stage === STAGE.USAGE_ENTERED)
        return role === ROLE.PATTERN;
    if (section === SHEET_SECTION.MATERIAL && stage === STAGE.TRIM_TECHNICAL_DONE)
        return role === ROLE.DESIGNER;
    return editScopeDef(scopeOfStage(section, stage)).roles.includes(role);
}
/** 附件按用途归属到域 */
export function scopeOfAttachment(kind) {
    const section = ATTACHMENT_SECTION[kind];
    if (section === SHEET_SECTION.QC)
        return EDIT_SCOPE.QC;
    if (section === SHEET_SECTION.PATTERN)
        return EDIT_SCOPE.SIZE_SPEC;
    if (section === SHEET_SECTION.MATERIAL)
        return EDIT_SCOPE.TRIM;
    return EDIT_SCOPE.DESIGN;
}
// ---------------------------------------------------------------------------
// 字段级的域划分
// ---------------------------------------------------------------------------
/**
 * 主材料一行里，哪些列属于哪个域。
 * 三个角色改同一张表的不同列，保存时按域合并：不属于你的列保留原值。
 */
export const MATERIAL_SCOPE_FIELDS = {
    [EDIT_SCOPE.FABRIC_BASE]: [
        'usedPartCd',
        'materialGroupCd',
        'materialCode',
        'brandMaterialCode',
        'remark',
        'materialNm',
        'materialAlias',
        'materialMemo',
        'librarySnapshot',
        'colorMatchPartCd',
        'tagColorCd',
        'tagColors',
        'vendorNm',
        'composition',
        'finishing',
    ],
    [EDIT_SCOPE.FABRIC_PROD]: ['width', 'unit', 'weight', 'weightUnit'],
    [EDIT_SCOPE.CONSUMPTION]: ['yardage', 'realYardage'],
    [EDIT_SCOPE.PRICE]: ['unitPrice', 'unitPriceCurrency'],
};
/** 辅材料按尺码的明细里，属于单耗、单价的列。其余列（以及行的增删）都归 TRIM。 */
export const SUB_MATERIAL_SIZE_SCOPE_FIELDS = {
    [EDIT_SCOPE.TRIM_TECHNICAL]: ['gauge', 'gaugeUnit', 'specText', 'isUsed'],
    [EDIT_SCOPE.TRIM_CONSUMPTION]: ['yardage', 'realYardage'],
    [EDIT_SCOPE.PRICE]: ['unitPrice', 'currency'],
};
export const SUB_MATERIAL_TECHNICAL_FIELDS = ['colorMatchPartCd', 'memo', 'isCommon', 'tagColors', 'yardageUnit'];
export const SIZE_DEVIATION_FIELDS = ['tolLeft', 'tolRight', 'tolPlus', 'tolMinus'];
//# sourceMappingURL=edit-scopes.js.map